import React, { useEffect, useState } from 'react';
import { Logo } from '@insight-health-ai/roseate/components';
import { Modal as AntModal, Radio } from 'antd';
import { Form, Formik } from 'formik';
import { toast } from '@insight-health-ai/roseate/components/toast';
import { CommonFormProps } from '.';
import { analytics } from 'src/services/analytics';
import { getAppointmentDetailsForAnalytics } from 'src/utils';

type Props = CommonFormProps;

export const PFDI = ({ isOpen, loading, appointment, form, onSave }: Props) => {
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);

  const pfdiResponse = (form.content as { table_content: any[] }).table_content;

  const [totalPfdiQuestions, setTotalPfdiQuestions] = useState(0);

  useEffect(() => {
    let total = 0;
    pfdiResponse.forEach((section) => {
      if (section.section_content && section.section_content.length > 0) {
        total += section.section_content.length;
      }
    });
    setTotalPfdiQuestions(total);
  }, [pfdiResponse]);

  const handleOptionChange = (field: any, question: any, option: any) => {
    const newAnswer = {
      section_header: field.title,
      question: question.title,
      answer: option,
      questionNumber: question.number,
    };

    const existingIndex = selectedOptions.findIndex(
      (item) => item.questionNumber === question.number
    );

    if (existingIndex !== -1) {
      const updatedOptions = [...selectedOptions];
      updatedOptions[existingIndex] = newAnswer;
      setSelectedOptions(updatedOptions);
    } else {
      setSelectedOptions((prevOptions) => [...prevOptions, newAnswer]);
    }
  };

  const sections: Record<string, any[]> = {};
  selectedOptions.forEach((option) => {
    if (!sections[option.section_header]) {
      sections[option.section_header] = [];
    }
    sections[option.section_header].push(option);
  });
  const scoreMap: Record<string, number> = {
    No: 0,
    'Not at all': 1,
    Somewhat: 2,
    Moderately: 3,
    'Quite a bit': 4,
  };
  let summaryScore = 0;
  let totalQuestionsAnswered = 0;
  let roundedSummaryScore = '0';

  const sectionScores: Record<string, string> = {};

  for (const section in sections) {
    let sectionScore = 0;
    const numQuestions = sections[section].length;
    totalQuestionsAnswered += numQuestions;
    sections[section].forEach((option) => {
      sectionScore += scoreMap[option.answer];
    });
    const averageScore = numQuestions > 0 ? sectionScore / numQuestions : 0;
    const finalSectionScore = averageScore * 25;
    sectionScores[section] = finalSectionScore.toFixed(2);
    summaryScore += finalSectionScore;
    roundedSummaryScore = summaryScore.toFixed(2);
  }

  const pfdiData = {
    response: selectedOptions,
    summaryScore: roundedSummaryScore,
    sectionScores: sectionScores,
  };

  const handleSubmitAddPFDI = async () => {
    analytics.track(
      'Clicked Submit In PFDI Form',
      getAppointmentDetailsForAnalytics(appointment!)
    );

    const allQuestionsAnswered = totalPfdiQuestions === totalQuestionsAnswered;
    if (allQuestionsAnswered) {
      await onSave({ response: pfdiData, form_type: 'PFDI_Form' });
    } else {
      toast.error('Please fill all the fields before proceeding');
    }
  };
  const renderFields = (fields: any[]) => {
    return fields.map((field, index) => (
      <div key={index}>
        <div
          className={
            field.type === 'section_header'
              ? 'section-heading-text'
              : field.type === 'form_header'
                ? 'heading-text'
                : ''
          }
        >
          {field.title}
        </div>
        <div
          className={
            field.type === 'section_header'
              ? 'subheading-text'
              : field.type === 'form_header'
                ? 'instruction-text'
                : ''
          }
        >
          {field.sub_title.includes('Instructions:') ||
          field.sub_title.includes('how much they bother you') ? (
            <>
              {field.sub_title
                .split(/(Instructions:|how much they bother you.)/)
                .map((part: string, index: number) => (
                  <React.Fragment key={index}>
                    {index % 2 === 1 && <b>{part}</b>}
                    {index % 2 === 0 && part}
                  </React.Fragment>
                ))}
            </>
          ) : (
            field.sub_title
          )}
        </div>
        {/* Render questions if present */}
        {field.question && (
          <div>
            {field.question.map((question: any, qIndex: number) => (
              <div key={qIndex}>
                <p className={question.type === 'label' ? 'ques-text' : ''}>
                  {question.title}
                  <span className="mandatory-star">*</span>
                </p>
                {question.options && (
                  <div>
                    {question.options && (
                      <div>
                        <Radio.Group
                          value={
                            selectedOptions.find(
                              (option) =>
                                option.questionNumber === question.number
                            )?.answer
                          }
                          onChange={(e) =>
                            handleOptionChange(field, question, e.target.value)
                          }
                        >
                          {question.options.map(
                            (option: any, oIndex: number) => (
                              <Radio key={oIndex} value={option.optionText}>
                                {option.optionText}
                              </Radio>
                            )
                          )}
                        </Radio.Group>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    ));
  };

  const [fields, setFields] = useState<any[]>([]);

  useEffect(() => {
    const transformedArray = pfdiResponse?.map((section) => {
      const sectionInfo = {
        title: section?.section_header || section?.form_heading,
        type: section?.type,
        sub_title: section?.section_sub_header || section?.form_instructions,
        question: section?.section_content?.map((question: any) => {
          const questionInfo = {
            number: question?.question_no,
            type: question?.type,
            title: question?.question,
            options: question?.question_options?.map((option: any) => ({
              optionText: option?.option_text,
              value: option?.value,
              type: option?.input_type,
            })),
          };
          return questionInfo;
        }),
      };
      return sectionInfo;
    });

    setFields(transformedArray);
  }, []);

  return (
    <AntModal
      open={isOpen}
      className={'PFDI_modal'}
      cancelText={null}
      closable={false}
      centered={true}
      footer={null}
    >
      <div className="modal____body">
        <div className="logo-container">
          <Logo className="h-8" />
        </div>
        {/** @ts-ignore */}
        <Formik>
          {() => (
            <Form className="modal__form font-sans">
              {renderFields(fields)}
            </Form>
          )}
        </Formik>
        {
          <div>
            <button
              disabled={loading}
              type="submit"
              onClick={() => handleSubmitAddPFDI()}
              className="form_submit"
            >
              Submit
            </button>
          </div>
        }
      </div>
    </AntModal>
  );
};
